import React from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Truck, Wallet, CreditCard, PackageCheck, Clock, Ban, HelpCircle } from "lucide-react";

const Step = ({ n, title, text }) => (
  <div className="flex gap-4" data-testid={`returns-step-${n}`}>
    <div className="shrink-0 w-9 h-9 rounded-full bg-[#E63946] text-white font-bold flex items-center justify-center">{n}</div>
    <div>
      <div className="font-semibold text-slate-800 dark:text-slate-100">{title}</div>
      <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{text}</div>
    </div>
  </div>
);

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6">
    <div className="flex items-center gap-2.5 mb-3">
      <div className="w-9 h-9 rounded-full bg-[#1D3557]/10 text-[#1D3557] dark:text-[#D4AF37] flex items-center justify-center"><Icon size={18} /></div>
      <h3 className="font-display font-bold text-lg">{title}</h3>
    </div>
    {children}
  </div>
);

export default function ReturnsPolicy() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="returns-policy-page">
      {/* hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1D3557] via-[#17284a] to-[#0c1830] text-white p-8 sm:p-10">
        <div className="absolute -top-16 right-0 w-64 h-64 rounded-full bg-[#E63946]/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
            <RotateCcw size={16} /> Returns & Exchange
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Easy 7-Day Returns</h1>
          <p className="text-slate-300 mt-3 max-w-2xl">
            Not the right fit? No worries. Bull Creations offers a hassle-free <span className="font-semibold text-white">7-day return & exchange</span> window
            from the date of delivery, with <span className="font-semibold text-white">free doorstep pickup</span> across India & the GCC.
          </p>
        </div>
      </div>

      {/* quick highlights */}
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5 flex items-center gap-3" data-testid="returns-highlight-window">
          <Clock className="text-[#E63946]" /> <div><div className="font-bold">7 Days</div><div className="text-xs text-slate-500">From delivery date</div></div>
        </div>
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5 flex items-center gap-3" data-testid="returns-highlight-pickup">
          <Truck className="text-[#E63946]" /> <div><div className="font-bold">Free Pickup</div><div className="text-xs text-slate-500">We arrange collection</div></div>
        </div>
        <div className="rounded-2xl border bg-white dark:bg-slate-900 p-5 flex items-center gap-3" data-testid="returns-highlight-refund">
          <Wallet className="text-[#E63946]" /> <div><div className="font-bold">Fast Refunds</div><div className="text-xs text-slate-500">Your choice of method</div></div>
        </div>
      </div>

      {/* how it works */}
      <div className="rounded-2xl border bg-white dark:bg-slate-900 p-6 mt-8">
        <h2 className="font-display font-bold text-xl mb-5">How a return works</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <Step n={1} title="Raise a request" text="Go to My Orders → select the item → tap Return / Exchange within 7 days of delivery, or email care@bullcreations.com." />
          <Step n={2} title="Free pickup scheduled" text="Our courier partner collects the parcel from your doorstep in 1–3 business days. Keep the item packed with its tags." />
          <Step n={3} title="Quality check" text="Once we receive and inspect the item (24–48 hours), your return is approved." />
          <Step n={4} title="Refund or exchange" text="We process your refund or ship the replacement size/colour, subject to availability." />
        </div>
      </div>

      {/* detail cards */}
      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <Card icon={PackageCheck} title="Eligibility">
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 list-disc pl-5">
            <li>Return requested within <b>7 days</b> of delivery.</li>
            <li>Item is <b>unused, unwashed and undamaged</b>.</li>
            <li>Original <b>tags, labels and packaging</b> are intact.</li>
            <li>Proof of purchase / order ID is available.</li>
          </ul>
        </Card>

        <Card icon={Ban} title="Non-returnable items">
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 list-disc pl-5">
            <li>Innerwear, socks and other hygiene-sensitive products.</li>
            <li>Customised / personalised <b>"With Logo"</b> orders.</li>
            <li>Items marked <b>Final Sale</b> or bought on clearance.</li>
            <li>Products returned without original tags or packaging.</li>
          </ul>
        </Card>

        <Card icon={CreditCard} title="Refunds">
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">Choose how you'd like your money back at the time of return:</p>
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 list-disc pl-5">
            <li><b>Original payment method</b> — credited in <b>5–7 business days</b> after the quality check.</li>
            <li><b>Bull Family Wallet credit</b> — instant store credit (usually within <b>24 hours</b>) you can reuse on any order.</li>
          </ul>
          <p className="text-xs text-slate-400 mt-3">Shipping charges, if any, are non-refundable except for damaged or wrong items.</p>
        </Card>

        <Card icon={RotateCcw} title="Exchanges">
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300 list-disc pl-5">
            <li>Exchange for a different <b>size or colour</b> of the same product, subject to stock.</li>
            <li>If the item is out of stock, we issue a full refund instead.</li>
            <li>One free exchange per item; replacement ships once pickup is collected.</li>
          </ul>
        </Card>
      </div>

      {/* damaged / wrong item */}
      <div className="rounded-2xl border border-dashed border-[#E63946]/40 bg-[#E63946]/5 p-6 mt-6">
        <h3 className="font-display font-bold text-lg mb-2">Received a damaged or wrong item?</h3>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          We're sorry! Report it within <b>48 hours</b> of delivery with a photo and we'll arrange a free replacement or a full refund
          including any shipping charges — no questions asked.
        </p>
      </div>

      {/* help */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl bg-[#1D3557] text-white p-6 mt-8">
        <div className="flex items-center gap-3">
          <HelpCircle className="text-[#D4AF37]" />
          <div>
            <div className="font-semibold">Need help with a return?</div>
            <div className="text-sm text-slate-300">Email care@bullcreations.com or call +91 421 000 0000 (Mon–Sat, 10am–7pm IST).</div>
          </div>
        </div>
        <Link to="/orders" data-testid="returns-go-orders" className="h-11 px-6 rounded-full bg-[#E63946] hover:bg-[#d12836] font-semibold flex items-center transition-colors">
          Go to My Orders
        </Link>
      </div>
    </div>
  );
}
